"use client";

import { OtpPopup } from "@/components/ui/otp-popup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUserActions } from "@/contexts/UserContext";
import { Link, useRouter } from "@/i18n/routing";
import { stripLocaleFromPath } from "@/lib/i18n-utils";
import { adminLogin } from "@/services/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useTranslations } from "next-intl";
import { useOAuth } from "@/hooks/useOAuth";

interface GraphQLErrorType {
  graphQLErrors?: Array<{
    extensions?: {
      code?: string;
    };
    message?: string;
  }>;
}

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm() {
  const t = useTranslations("loginForm");
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const { setUser } = useUserActions();
  const {
    handleGoogleLogin,
    isLoading: oauthLoading,
    error: oauthError,
    setError: setOAuthError,
  } = useOAuth();
  const [isClient, setIsClient] = useState(false);

  // Get the callback URL or default to dashboard
  const rawCallbackUrl = searchParams?.get("callbackUrl") || "/dashboard";
  // Strip locale prefix since i18n router will add it automatically
  const callbackUrl = stripLocaleFromPath(rawCallbackUrl);

  // Ensure we're on client side for OAuth
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Handle OAuth errors
  useEffect(() => {
    if (oauthError) {
      setError(oauthError);
      setOAuthError(null);
    }
  }, [oauthError, setOAuthError]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await adminLogin(data.email, data.password);
      const loginData = response.data.staffAreaLogin;

      // Check if 2FA is required
      if (loginData?.requiresOtp && !loginData.user) {
        setPendingCredentials({ email: data.email, password: data.password });
        setShowOtpPopup(true);
      } else if (loginData.user && loginData.token) {
        // Login successful - use NextAuth to create session
        const result = await signIn("credentials", {
          email: data.email,
          password: "oauth_login", // Special marker for OAuth login
          accessToken: loginData.token,
          refreshToken: loginData.refreshToken,
          userId: loginData.user.id,
          firstName: loginData.user.firstName,
          lastName: loginData.user.lastName,
          redirect: false,
        });

        if (result?.ok) {
          if (loginData.user) {
            setUser(loginData.user); // Use setUser instead of setUserPartial since we now have complete data
          }
          router.push(callbackUrl);
        } else {
          setError(t("errors.sessionCreationFailed"));
        }
      } else {
        setError(t("errors.invalidCredentials"));
      }
    } catch (err: unknown) {
      console.error("Login error:", err);

      // Handle specific GraphQL errors
      const graphqlError = (err as GraphQLErrorType)?.graphQLErrors?.[0];

      // Check for network errors (common on mobile)
      if (err && typeof err === "object" && "networkError" in err) {
        setError(t("errors.networkConnectionFailed"));
      } else if (
        err &&
        typeof err === "object" &&
        "message" in err &&
        (err.message as string).toLowerCase().includes("timeout")
      ) {
        setError(t("errors.requestTimedOut"));
      } else if (graphqlError) {
        switch (graphqlError.extensions?.code) {
          case "INVALID_CREDENTIALS":
            setError(t("errors.invalidCredentials"));
            break;
          case "NO_PERMISSIONS":
            setError(t("errors.noPermissions"));
            break;
          case "USER_NOT_ACTIVE":
            setError(t("errors.userNotActive"));
            break;
          default:
            setError(graphqlError.message || t("errors.loginFailed"));
        }
      } else {
        setError(t("errors.loginFailedGeneric"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (otp: string) => {
    if (!pendingCredentials) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await adminLogin(
        pendingCredentials.email,
        pendingCredentials.password,
        otp
      );
      const loginData = response.data.staffAreaLogin;

      if (loginData.user && loginData.token) {
        // OTP verification successful - create session
        const result = await signIn("credentials", {
          email: pendingCredentials.email,
          password: "oauth_login", // Special marker for OAuth login
          accessToken: loginData.token,
          refreshToken: loginData.refreshToken,
          userId: loginData.user.id,
          firstName: loginData.user.firstName,
          lastName: loginData.user.lastName,
          redirect: false,
        });

        if (result?.ok) {
          // Set complete user data from enhanced login response for instant UI update
          if (loginData.user) {
            setUser(loginData.user); // Use setUser instead of setUserPartial since we now have complete data
          }
          setShowOtpPopup(false);
          setPendingCredentials(null);
          router.push(callbackUrl);
        } else {
          setError(t("errors.sessionCreationFailed"));
        }
      } else {
        setError(t("otp.invalidCode"));
      }
    } catch (err: unknown) {
      // Handle specific GraphQL errors
      const graphqlError = (err as GraphQLErrorType)?.graphQLErrors?.[0];
      if (graphqlError) {
        switch (graphqlError.extensions?.code) {
          case "OTP_INVALID":
            setError(t("otp.invalidCode"));
            break;
          case "OTP_EXPIRED":
            setError(t("otp.expiredCode"));
            // Close OTP popup and reset to login form
            setShowOtpPopup(false);
            setPendingCredentials(null);
            break;
          default:
            setError(graphqlError.message || t("otp.verificationFailed"));
        }
      } else {
        setError(t("otp.verificationFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpResend = async () => {
    if (!pendingCredentials) return;
    // Re-attempt login to trigger new OTP
    try {
      await adminLogin(pendingCredentials.email, pendingCredentials.password);
    } catch (err) {
      console.error("OTP resend error:", err);
    }
  };

  const handleOtpClose = () => {
    setShowOtpPopup(false);
    setPendingCredentials(null);
    setError(null);
  };

  return (
    <>
      <form
        className="UserLoginForm"
        style={{
          background:
            "linear-gradient(to bottom right, hsl(var(--primary) / 0.15), hsl(var(--background)), hsl(var(--secondary) / 0.15))",
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="p-6">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center text-center space-y-3 mb-2">
              <div
                className="w-16 h-1 rounded-full mb-2"
                style={{
                  background:
                    "linear-gradient(to right, transparent, hsl(var(--primary)), transparent)",
                }}
              ></div>
              <h1 className="text-3xl font-bold tracking-tight text-primary">
                {t("title")}
              </h1>
              <p className="text-muted-foreground text-sm md:text-base max-w-sm">
                {t("subtitle")}
              </p>
            </div>

            {error && (
              <div className="bg-destructive/10 dark:bg-destructive/20 text-destructive border border-destructive/30 dark:border-destructive/50 px-4 py-3 rounded-lg text-sm font-medium animate-in fade-in slide-in-from-top-2 duration-300 shadow-sm">
                {error}
              </div>
            )}

            <div className="grid gap-3">
              <Label htmlFor="email">{t("email.label")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("email.placeholder")}
                {...register("email")}
                className={errors.email ? "border-destructive" : ""}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-destructive text-sm">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="grid gap-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("password.label")}</Label>
                <Link
                  href="/reset-password"
                  className="text-sm text-primary hover:text-primary/80 underline underline-offset-4 font-medium transition-colors"
                >
                  {t("forgotPassword")}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder={t("password.placeholder")}
                {...register("password")}
                className={errors.password ? "border-destructive" : ""}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="text-destructive text-sm">
                  {errors.password.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 dark:hover:bg-primary/80 text-primary-foreground font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 mt-2"
              disabled={isLoading || oauthLoading === "google"}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground"></span>
                  {t("submit.loading")}
                </span>
              ) : (
                t("submit.default")
              )}
            </Button>

            {isClient && (
              <>
                <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                  <span className="bg-card text-muted-foreground relative z-10 px-2">
                    {t("or") || "Or"}
                  </span>
                </div>

                <Button
                  variant="outline"
                  type="button"
                  className="w-full"
                  onClick={() => handleGoogleLogin(callbackUrl)}
                  disabled={isLoading || oauthLoading === "google"}
                >
                  {oauthLoading === "google" ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                      {t("oauth.loading") || "Loading..."}
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="w-4 h-4 mr-2"
                      >
                        <path
                          d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                          fill="currentColor"
                        />
                      </svg>
                      {t("oauth.google") || "Login with Google"}
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </form>

      <OtpPopup
        isOpen={showOtpPopup}
        onClose={handleOtpClose}
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        email={pendingCredentials?.email}
        isLoading={isLoading}
        error={error}
      />
    </>
  );
}
